const NETWORK_ID = process.env.VUE_APP_NETWORK_ID || "3"

const promisify = fn =>
  new Promise((resolve, reject) =>
    fn((err, res) => {
      if (err) reject(err)
      else resolve(res)
    })
  )

const deferred = () => {
  let res, rej

  const promise = new Promise((resolve, reject) => {
    res = resolve
    rej = reject
  })

  promise.resolve = res
  promise.reject = rej

  return promise
}

const state = {
  available: false,
  account: null,
  mtxBalance: null,
  network: null,
  instance: null,
  ready: false,
  lock: {
    enabled: false,
    callback: null,
    deferred: null
  }
}

const mutations = {
  SET_AVAILABLE(state, payload) {
    state.available = payload
  },

  SET_INSTANCE(state, payload) {
    state.instance = payload
  },

  SET_LOCK({ lock }, payload) {
    lock.enabled = true
    lock.callback = payload
    lock.deferred = deferred()
  },

  CLEAR_LOCK({ lock }) {
    if (lock.deferred) {
      lock.deferred.resolve()
    }

    lock.enabled = false
    lock.callback = null
    lock.deferred = null
  },

  UPDATE_MTX_BALANCE(state, payload) {
    state.mtxBalance = payload
  },

  UPDATE_WEB3(state, payload) {
    Object.assign(state, payload)

    const { available, account, network } = state
    const web3Ready = available && !!account && network === NETWORK_ID

    const { user } = this.state
    const userReady = user.ethAddress && user.email

    state.ready = web3Ready && userReady
  }
}

const actions = {
  async updateWeb3({ state, rootState, commit, dispatch }) {
    if (!state.instance) return
    const web3 = state.instance()
    const prevAccount = state.account

    let [[account], network] = await Promise.all([
      promisify(web3.eth.getAccounts),
      promisify(web3.version.getNetwork)
    ])

    if (account) {
      account = account.toLowerCase()
    }

    if (network !== NETWORK_ID && rootState.user.ethAddress) {
      dispatch("logout")
    }

    commit("UPDATE_WEB3", { account, network })

    if (account !== prevAccount) {
      dispatch("updateMtxBalance")
    }
  },

  async checkWeb3({ state, commit, dispatch }) {
    const available = typeof window.ethereum !== "undefined"

    if (available !== state.available) {
      commit("SET_AVAILABLE", available)
    }

    if (available && !state.instance) {
      commit("SET_INSTANCE", () => window.web3)

      // contracts store module
      dispatch("createContracts")

      window.ethereum.on("accountsChanged", () => dispatch("checkWeb3"))
      window.ethereum.on("networkChanged", () => dispatch("checkWeb3"))
    }

    await dispatch("updateWeb3")

    const { lock, ready } = state

    if (lock.enabled && ready) {
      if (typeof lock.callback === "function") {
        await lock.callback()
      }
      commit("CLEAR_LOCK")
    }
  },

  clearLock({ commit }) {
    commit("CLEAR_LOCK")
  },

  async requireWeb3({ state, commit, dispatch }, payload) {
    commit("SET_LOCK", payload)
    await dispatch("checkWeb3")
    await state.lock.deferred
  },

  async updateMtxBalance({ state, rootState, commit }) {
    if (!state.account || state.network !== NETWORK_ID) return

    const Token = await rootState.contracts.Token
    const balance = await Token.balanceOf(state.account)
    commit("UPDATE_MTX_BALANCE", balance)
  }
}

export default {
  state,
  mutations,
  actions
}
